// NOTE: Age verification is enforced client-side.
// For production, also validate date_of_birth
// server-side via a Supabase Edge Function or
// database trigger to prevent bypass.
// Current implementation is sufficient for MVP.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { track } from '../services/heliosTelemetry';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { haptic } from '../lib/haptics';
import {
  AuthGateShell,
  AuthUnderlineInput,
  AuthPasswordInput,
  AuthGoldButton,
} from './AuthGate';

const MONTHS = [
  { v: 1, label: 'Jan' },
  { v: 2, label: 'Feb' },
  { v: 3, label: 'Mar' },
  { v: 4, label: 'Apr' },
  { v: 5, label: 'May' },
  { v: 6, label: 'Jun' },
  { v: 7, label: 'Jul' },
  { v: 8, label: 'Aug' },
  { v: 9, label: 'Sep' },
  { v: 10, label: 'Oct' },
  { v: 11, label: 'Nov' },
  { v: 12, label: 'Dec' },
] as const;

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function isValidCalendarDate(y: number, m: number, d: number): boolean {
  if (m < 1 || m > 12 || d < 1) return false;
  const max = daysInMonth(y, m);
  return d <= max;
}

function calcAge(y: number, m: number, d: number): number {
  const today = new Date();
  let age = today.getFullYear() - y;
  const mo = today.getMonth() + 1 - m;
  if (mo < 0 || (mo === 0 && today.getDate() < d)) age--;
  return age;
}

function formatDob(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

const selectClass =
  'w-full min-w-0 rounded-xl border bg-[var(--color-surface)] px-3 py-3 font-body text-sm text-[var(--color-ink-1)] outline-none transition-colors appearance-none cursor-pointer';
const selectBorderNormal = 'border-[var(--color-border)] focus:border-[var(--color-cinder)]';
const selectBorderError = 'border-[var(--color-status-critical)]';

// Modal-tier surface for the age-gate (one-shot focused interaction). Per v3.1
// Calm Principle #2 (surfaces should disappear), this uses the modal token —
// no gradient, no depth-* refs.
const cardSurface = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
} as const;

export default function Signup() {
  const [ageGatePassed, setAgeGatePassed] = useState(false);
  const [underageBlocked, setUnderageBlocked] = useState(false);
  const [dobString, setDobString] = useState('');
  const [month, setMonth] = useState('');
  const [day, setDay] = useState('');
  const [year, setYear] = useState('');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [showTosModal, setShowTosModal] = useState<'tos' | 'privacy' | null>(null);

  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo(() => {
    const minY = currentYear - 100;
    const maxY = currentYear - 13;
    const out: number[] = [];
    for (let y = maxY; y >= minY; y--) out.push(y);
    return out;
  }, [currentYear]);

  const dayOptions = useMemo(() => {
    if (!month || !year) return Array.from({ length: 31 }, (_, i) => i + 1);
    const y = parseInt(year, 10);
    const m = parseInt(month, 10);
    const n = daysInMonth(y, m);
    return Array.from({ length: n }, (_, i) => i + 1);
  }, [month, year]);

  const allDobSelected = Boolean(month && day && year);
  const inputError = underageBlocked;

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setEmailError(null);
    setConfirmError(null);
    if (password !== confirmPassword) {
      haptic('actionFailed');
      setConfirmError("Passwords don't match");
      return;
    }
    if (!tosAccepted) {
      haptic('actionFailed');
      setEmailError('Please accept the Terms of Service to continue.');
      return;
    }
    setLoading(true);
    const { data, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
    });
    setLoading(false);
    if (signUpError) {
      console.error('[Signup] signUp error:', signUpError);
      haptic('actionFailed');
      setEmailError(signUpError.message);
      return;
    }
    track('signup_completed', { user_id: data?.user?.id });
    const newUser = data.user;
    if (newUser && dobString) {
      const { error: profileErr } = await supabase
        .from('profiles')
        .update({
          date_of_birth: dobString,
          age_verified: true,
        })
        .eq('id', newUser.id);
      if (profileErr) console.error('Profile DOB update:', profileErr);
    }
    const code = referralCode.trim().toUpperCase();
    if (code) {
      try {
        localStorage.setItem('helios_pending_referral_code', code);
      } catch {
        /* ignore */
      }
    }
    haptic('prAchieved');
    setSuccess(true);
  }

  function handleAgeContinue() {
    if (!allDobSelected) return;
    const y = parseInt(year, 10);
    const m = parseInt(month, 10);
    const d = parseInt(day, 10);
    if (!isValidCalendarDate(y, m, d)) return;
    const age = calcAge(y, m, d);
    if (age < 18) {
      haptic('actionFailed');
      setUnderageBlocked(true);
      return;
    }
    haptic('buttonTap');
    setDobString(formatDob(y, m, d));
    setAgeGatePassed(true);
  }

  if (!ageGatePassed) {
    return (
      <div
        className="relative flex min-h-dvh min-h-screen flex-col items-center justify-center overflow-y-auto px-6 py-16"
        style={{ backgroundColor: 'var(--color-canvas)' }}
      >
        <div className="relative z-10 mx-auto w-full max-w-sm min-w-0">
          <div className="mb-8 text-center">
            <h1 className="font-display"
              style={{
                fontWeight: 400,
                fontSize: '40px',
                letterSpacing: '-0.02em',
                margin: 0,
                lineHeight: 1.1,
                color: 'var(--color-ink-1)',
              }}
            >
              HELIOS
            </h1>
            <p className="mt-3 font-body text-sm font-medium uppercase tracking-widest text-[var(--color-ink-3)]">
              Your physique, analyzed.
            </p>
          </div>

          <div className="rounded-xl p-6" style={cardSurface}>
            <h2
              className="mt-6 text-center font-display"
              style={{
                fontWeight: 400,
                fontSize: '28px',
                letterSpacing: '-0.018em',
                color: 'var(--color-ink-1)',
                margin: 0,
                lineHeight: 1.2,
              }}
            >
              Before we begin
            </h2>
            <p className="font-body mt-2 text-sm leading-relaxed text-[var(--color-text-secondary)]">
              You must be 18 or older to use HELIOS. We ask because our app involves physique
              analysis — we take safety seriously.
            </p>

            <label className="mt-6 block font-body text-xs uppercase tracking-widest text-[var(--color-text-secondary)]">
              Date of birth
            </label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              <select
                value={month}
                onChange={(e) => {
                  setMonth(e.target.value);
                  setDay('');
                  setUnderageBlocked(false);
                }}
                className={`${selectClass} ${inputError ? selectBorderError : selectBorderNormal}`}
                aria-label="Month"
              >
                <option value="">Month</option>
                {MONTHS.map((mo) => (
                  <option key={mo.v} value={String(mo.v)}>
                    {mo.label}
                  </option>
                ))}
              </select>
              <select
                value={day}
                onChange={(e) => {
                  setDay(e.target.value);
                  setUnderageBlocked(false);
                }}
                className={`${selectClass} ${inputError ? selectBorderError : selectBorderNormal}`}
                aria-label="Day"
              >
                <option value="">Day</option>
                {dayOptions.map((dv) => (
                  <option key={dv} value={String(dv)}>
                    {dv}
                  </option>
                ))}
              </select>
              <select
                value={year}
                onChange={(e) => {
                  setYear(e.target.value);
                  setDay('');
                  setUnderageBlocked(false);
                }}
                className={`${selectClass} ${inputError ? selectBorderError : selectBorderNormal}`}
                aria-label="Year"
              >
                <option value="">Year</option>
                {yearOptions.map((yv) => (
                  <option key={yv} value={String(yv)}>
                    {yv}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-3 flex items-start gap-2 text-[var(--color-text-secondary)]">
              <Lock className="mt-0.5 shrink-0" size={12} strokeWidth={2} aria-hidden />
              <p className="font-body text-xs leading-relaxed">
                Your date of birth is never shared or displayed publicly.
              </p>
            </div>

            {underageBlocked && (
              <div className="mt-4">
                <p className="font-body text-sm text-[var(--color-status-critical)]">
                  You must be 18 or older to create an account.
                </p>
                <p className="font-body mt-2 text-xs text-[var(--color-text-secondary)]">
                  HELIOS involves physique photo analysis. We restrict access to protect minors.
                </p>
                <p className="font-body mt-4 text-center text-sm italic text-[var(--color-text-secondary)]">
                  Come back when you&apos;re 18.
                </p>
              </div>
            )}

            {!underageBlocked && (
              <button
                type="button"
                disabled={!allDobSelected}
                onClick={handleAgeContinue}
                className="mt-6 w-full rounded-full py-4 font-body text-sm font-semibold tracking-[0.02em] transition-colors duration-150 ease-out hover:bg-[var(--color-cinder-muted)] active:bg-[var(--color-cinder-muted)] disabled:cursor-not-allowed disabled:opacity-60"
                style={{
                  background: 'var(--color-cinder)',
                  color: '#F2ECE0',
                  border: 'none',
                  minHeight: '48px',
                }}
              >
                Continue
              </button>
            )}
          </div>

          <p className="mt-10 text-center font-body text-sm text-[var(--color-text-secondary)]">
            Already have an account?{' '}
            <Link
              to="/login"
              className="text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-cinder)]"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
    <AuthGateShell
      heading="Create account"
      footer={
        <p className="font-body text-sm text-[var(--color-text-secondary)]">
          Already have an account?{' '}
          <Link
            to="/login"
            className="text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-cinder)]"
          >
            Sign in
          </Link>
        </p>
      }
    >
      {success ? (
        <p className="font-body text-center text-sm leading-relaxed text-[var(--color-text-secondary)]">
          Check your email to confirm your account.
        </p>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        >
          <form onSubmit={handleSubmit} className="mx-auto w-full min-w-0 max-w-sm space-y-4">
            <AuthUnderlineInput
              type="email"
              placeholder="Email"
              value={email}
              onChange={setEmail}
              autoComplete="email"
              enterKeyHint="next"
              error={emailError}
            />
            <AuthPasswordInput
              placeholder="Password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              enterKeyHint="next"
              error={null}
            />
            <AuthPasswordInput
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              autoComplete="new-password"
              enterKeyHint="go"
              error={confirmError}
            />
            <div className="w-full min-w-0 max-w-sm">
              <label className="mb-1 block font-body text-xs text-[var(--color-text-secondary)]">
                Referral code (optional)
              </label>
              <input
                type="text"
                placeholder="HE-XXXXXX"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value)}
                autoComplete="off"
                className="w-full border-0 border-b border-[var(--color-border)] bg-transparent py-2 font-body text-[var(--color-ink-1)] placeholder:text-[var(--color-ink-4)] outline-none transition-colors focus:border-[var(--color-cinder)]"
              />
            </div>
            <div className="flex items-start gap-3 mt-4">
              <input
                type="checkbox"
                id="tos"
                checked={tosAccepted}
                onChange={(e) => setTosAccepted(e.target.checked)}
                className="mt-1 shrink-0 accent-[var(--color-cinder)]"
              />
              <label htmlFor="tos" className="font-body text-xs text-[var(--color-text-secondary)] leading-relaxed">
                I agree to the{' '}
                <button type="button" onClick={() => setShowTosModal('tos')} className="text-[var(--color-cinder)] underline">Terms of Service</button>
                {' '}and{' '}
                <button type="button" onClick={() => setShowTosModal('privacy')} className="text-[var(--color-cinder)] underline">Privacy Policy</button>
                . I understand that HELIOS provides training guidance, not medical advice.
              </label>
            </div>
            <AuthGoldButton disabled={loading || !tosAccepted}>
              {loading ? 'Creating account…' : 'Create account'}
            </AuthGoldButton>
            {!tosAccepted && (
              <p className="font-body text-center text-xs text-[#5A5650]">
                Check the box above to continue
              </p>
            )}
          </form>
        </motion.div>
      )}
    </AuthGateShell>

      {/* ToS / Privacy Modal */}
      <AnimatePresence>
        {showTosModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.8)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
            }}
            onClick={() => setShowTosModal(null)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 320 }}
              style={{
                background: 'var(--color-surface)',
                borderRadius: '20px 20px 0 0',
                width: '100%',
                maxWidth: '480px',
                maxHeight: '70vh',
                padding: '24px',
                overflowY: 'auto',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h2 style={{ color: 'var(--color-text-primary)', fontWeight: 700, fontSize: '16px', margin: 0 }}>
                  {showTosModal === 'tos' ? 'Terms of Service' : 'Privacy Policy'}
                </h2>
                <button
                  type="button"
                  onClick={() => setShowTosModal(null)}
                  aria-label="Close"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', padding: 0 }}
                >
                  <X size={20} aria-hidden />
                </button>
              </div>
              {showTosModal === 'tos' ? (
                <div style={{ color: 'var(--color-text-secondary)', fontSize: '13px', lineHeight: '1.7' }}>
                  <p>By using HELIOS, you agree to these terms:</p>
                  <p style={{ marginTop: '12px' }}><strong style={{ color: 'var(--color-text-primary)' }}>Use of Service.</strong> HELIOS provides AI-assisted physique analysis and training guidance for personal, non-commercial use. You must be 18 or older to use this service.</p>
                  <p style={{ marginTop: '12px' }}><strong style={{ color: 'var(--color-text-primary)' }}>Not Medical Advice.</strong> Content generated by HELIOS is for informational purposes only and does not constitute medical, nutritional, or professional fitness advice. Consult a qualified professional before beginning any fitness program.</p>
                  <p style={{ marginTop: '12px' }}><strong style={{ color: 'var(--color-text-primary)' }}>User Content.</strong> Photos uploaded for analysis are processed immediately and never stored on our servers.</p>
                  <p style={{ marginTop: '12px' }}><strong style={{ color: 'var(--color-text-primary)' }}>Account.</strong> You are responsible for maintaining the security of your account and all activity under it.</p>
                  <p style={{ marginTop: '12px' }}><strong style={{ color: 'var(--color-text-primary)' }}>Changes.</strong> We may update these terms at any time. Continued use constitutes acceptance.</p>
                </div>
              ) : (
                <div style={{ color: 'var(--color-text-secondary)', fontSize: '13px', lineHeight: '1.7' }}>
                  <p>Your privacy matters. Here is how we handle your data:</p>
                  <p style={{ marginTop: '12px' }}><strong style={{ color: 'var(--color-text-primary)' }}>Photos.</strong> Uploaded photos are analyzed in real time and never stored. They are deleted immediately after analysis.</p>
                  <p style={{ marginTop: '12px' }}><strong style={{ color: 'var(--color-text-primary)' }}>Profile Data.</strong> We store your profile information (age, goals, training preferences) to personalize your experience. This data is never sold or shared with third parties.</p>
                  <p style={{ marginTop: '12px' }}><strong style={{ color: 'var(--color-text-primary)' }}>Analysis Results.</strong> Your physique scores and progress data are stored securely and visible only to you.</p>
                  <p style={{ marginTop: '12px' }}><strong style={{ color: 'var(--color-text-primary)' }}>Authentication.</strong> We use Supabase for secure authentication. Passwords are never stored in plain text.</p>
                  <p style={{ marginTop: '12px' }}><strong style={{ color: 'var(--color-text-primary)' }}>Deletion.</strong> You may delete your account and all associated data at any time from your profile settings.</p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
